/**
 * TriggerJobModal Component
 *
 * Modal for manually triggering an ELT job with optional parameter overrides
 * and phase selection (all phases or transformation only).
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useTriggerJob } from '../hooks/useTriggerJob';
import type { EltJob, EltJobTriggerDTO, JobPhases } from '../../../types/elt-job.types';
import { PlayIcon } from '@heroicons/react/24/outline';

interface TriggerJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  eltJob: EltJob;
}

export const TriggerJobModal: React.FC<TriggerJobModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  eltJob,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [parameterOverrides, setParameterOverrides] = useState<Record<string, string>>({});
  const [selectedPhases, setSelectedPhases] = useState<JobPhases>('all');

  // Get job-level parameters directly
  const jobParameters = eltJob.parameters || [];

  const { mutate: triggerJob, isPending } = useTriggerJob(
    tenantId,
    eltJob.eltJobId,
    {
      onSuccess: (jobExecution) => {
        setSuccessMessage(`Job triggered successfully! Execution ID: ${jobExecution.jobExecutionCode}`);
        setErrorMessage('');
        setTimeout(() => {
          onClose();
          setSuccessMessage('');
          setParameterOverrides({});
          setSelectedPhases('all');
        }, 2000);
      },
      onError: (error) => {
        setErrorMessage(error);
        setSuccessMessage('');
      },
    }
  );

  const handleTrigger = () => {
    setErrorMessage('');
    setSuccessMessage('');

    const data: EltJobTriggerDTO = {
      phases: selectedPhases,
    };

    // Only include non-empty overrides
    const nonEmptyOverrides: Record<string, string> = {};
    Object.entries(parameterOverrides).forEach(([key, value]) => {
      if (value.trim()) {
        nonEmptyOverrides[key] = value.trim();
      }
    });

    if (Object.keys(nonEmptyOverrides).length > 0) {
      data.parameterOverrides = nonEmptyOverrides;
    }

    triggerJob(data);
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      setSuccessMessage('');
      setParameterOverrides({});
      setSelectedPhases('all');
      onClose();
    }
  };

  const handleParameterChange = (name: string, value: string) => {
    setParameterOverrides((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Run ELT Job</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Job Info */}
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-600">
              Running:{' '}
              <span className="font-medium">{eltJob.eltJobName}</span>
              <span className="ml-2 font-mono text-xs text-gray-500">
                ({eltJob.eltJobCode})
              </span>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {eltJob.queries.filter((q) => q.isActive).length} active queries
            </p>
          </div>

          {/* Phase Selection */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Execution Phases</h3>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="phases"
                  value="all"
                  checked={selectedPhases === 'all'}
                  onChange={() => setSelectedPhases('all')}
                  className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    All Phases
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Run extraction, load, and transformation phases
                  </span>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="phases"
                  value="transformation"
                  checked={selectedPhases === 'transformation'}
                  onChange={() => setSelectedPhases('transformation')}
                  className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    Transformation Only
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Skip extraction and load, run only transformation using data from the last successful load
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Parameter Overrides */}
          {jobParameters.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900">Parameter Overrides</h3>
              <p className="text-xs text-gray-500">
                Leave empty to use the default value defined for the job.
              </p>
              {jobParameters.map((parameter) => (
                <FormField
                  key={parameter.name}
                  label={parameter.name}
                  hint={`Default: ${parameter.defaultValue || '(empty)'}`}
                >
                  <Input
                    value={parameterOverrides[parameter.name] || ''}
                    onChange={(e) => handleParameterChange(parameter.name, e.target.value)}
                    placeholder={parameter.defaultValue}
                  />
                </FormField>
              ))}
            </div>
          )}

          {jobParameters.length === 0 && (
            <p className="text-sm text-gray-500">
              This job has no configurable parameters. It will run with the default configuration.
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleTrigger}
              loading={isPending}
              disabled={!eltJob.isActive}
            >
              <PlayIcon className="h-4 w-4 mr-1" />
              Run Job
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
